import { Component, inject, OnDestroy } from '@angular/core';
import { HasUnsavedChanges } from '../../../../guards/unsaved-changes.guard';
import { FormBuilder, FormGroup } from '@angular/forms';
import { partition } from 'src/app/modules/shared/utils/array.utils';
import { FAILURE_ID_PARAM, GO_TASK_URL, GO_URL, ID_PARAM, TASKS_URL } from '../../../../config/url.config';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpContext } from '@angular/common/http';
import { SKIP_ERROR_TOAST } from '../../../../interceptors/error.interceptor';
import { catchError, debounceTime, EMPTY, filter, finalize, retry, Subject, Subscription, switchMap, take, tap } from 'rxjs';
import { StoreService } from '../../../../services/store.service';
import { SpanMapModel, TowerMapModel } from '../../../../model/map.model';
import { isPseToCorrect, Task, TaskStatus } from '../../../go/model/task.model';
import { TasksService } from '../../../go/services/tasks.service';
import {
  getLastDefectNumber,
  getRegisteredDefects
} from '../../../go/utils/form.utils';
import {
  Control,
  FormEntry,
  FormEntryValue,
  getFormEntryControlName,
  getFormEntryInstanceKey,
  parseFormEntryInstanceKey
} from '../../../go/model/form.model';
import { ConnectionStatusService } from 'src/app/services/connection-status.service';
import { KeyValueItem, PdfBaseModel } from '../../../../model/general.model';
import { TranslateService } from '@ngx-translate/core';
import { userOptionLabel } from '../../../../utils/user.utils';
import { DATE_TIME_FORMAT, getLocalDate } from '../../../../utils/date.utils';
import { AttachmentService } from "../../../../services/attachment.service";
import { openFileInNewWindow } from "../../utils/attachment.utils";
import { DynamicFormService } from "../../../../services/dynamic-form.service";

@Component({
  selector: 'dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss']
})
export class DynamicFormComponent implements OnDestroy, HasUnsavedChanges {
  private readonly fb = inject(FormBuilder);
  private readonly tasksService = inject(TasksService);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(StoreService);
  private readonly router = inject(Router);
  protected readonly connectionStatusService = inject(ConnectionStatusService);
  private readonly translate = inject(TranslateService);
  private readonly attachmentsService = inject(AttachmentService);
  private readonly dynamicFormService = inject(DynamicFormService);

  private formListener$: Subscription;
  private autosaveSubscription: Subscription;
  private readonly task$ = new Subject<Task>();
  protected readonly config$ = this.task$.pipe(
    tap((data) => {
      this.initForm();

      setTimeout(() => {
        if (data.tower) {
          const tower: TowerMapModel = {
            ...data.tower,
            tasks: [data]
          }
          this.store.setPowerLineConfig({ towers: [tower], spans: [] });

          if (tower.linePdfList) {
            this.pdfFiles = [];
            this.pdfFiles = tower.linePdfList.filter(x => x.linePdfId);
          }
        }

        if (data.span) {
          const span: SpanMapModel = {
            ...data.span,
            tasks: [data]
          }
          this.store.setPowerLineConfig({ towers: [], spans: [span] });
          if (span.linePdf && span.linePdf.linePdfId) {
            this.pdfFiles = [];
            this.pdfFiles.push(span.linePdf);
          }
        }
      }, 10);
    if(data.project) {
      this.store.updateSelectedProject(data.project.id);
    }

    this.store.hideLoader();
    this.initializeTaskView(data);
    this.focusSyncIssueEntry();
    this.prepareCustomHeaders(data);
    this.status = data.status;
    this.controlsHaveDefaultValue = this.checkIfControlsHaveDefaultValue(data)
    })
  )
  protected form: FormGroup;
  private taskId!: number;
  protected failureId?: number;
  protected defectsLastNo: number = 0;
  protected readonly = true;
  protected submitLabel: string;
  protected showActionButtons = false;
  protected showCloseButton = false;
  protected showSubmitButton = false;
  protected showSaveButton = true;
  private status: TaskStatus;
  protected customHeaders: KeyValueItem[] = [];
  protected pdfFiles: PdfBaseModel[] = [];
  protected showFillInButton = false;
  private controlsHaveDefaultValue = false;
  private initialFormValue: unknown = null;
  protected isAutosaving$ = this.dynamicFormService.autosaving$;
  protected validationRefreshTick = 0;
  protected syncIssueEntryKey?: string;

  protected readonly GO_TASK_URL = GO_TASK_URL;
  protected readonly isPseToCorrect = isPseToCorrect;

  constructor() {
    this.store.showLoader();
    this.taskId = this.route.snapshot.params[ID_PARAM];
    this.failureId = parseInt(this.route.snapshot.params[FAILURE_ID_PARAM]);
    const issueEntryIdParam = this.route.snapshot.queryParamMap.get('syncIssueEntryId');
    const issueCloneIndexParam = this.route.snapshot.queryParamMap.get('syncIssueCloneIndex');
    const issueEntryId = Number(issueEntryIdParam);
    const issueCloneIndex = Number(issueCloneIndexParam);
    if (issueEntryIdParam !== null && Number.isFinite(issueEntryId)) {
      this.syncIssueEntryKey = getFormEntryInstanceKey(
        issueEntryId,
        issueCloneIndexParam !== null && Number.isFinite(issueCloneIndex) ? issueCloneIndex : undefined
      );
    }
    this.loadTask();
    this.store.hideProjectSelector();
  }

  private focusSyncIssueEntry(): void {
    if (!this.syncIssueEntryKey) {
      return;
    }
    const elementId = `form-entry-${this.syncIssueEntryKey.replace(':', '-')}`;
    setTimeout(() => {
      document.getElementById(elementId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 250);
  }

  private initForm() {
    this.initialFormValue = null;
    if (this.formListener$) {
      this.formListener$.unsubscribe();
    }
    if (this.autosaveSubscription) {
      this.autosaveSubscription.unsubscribe();
    }
    this.form = this.fb.group({});
    this.formListener$ = this.form.valueChanges.subscribe(value => {
        setTimeout(() => {
          const defects = getRegisteredDefects(value);
          this.defectsLastNo = getLastDefectNumber(defects);
        }, 0);
      });
    setTimeout(() => {
      this.initialFormValue = this.form.getRawValue();
      this.setupAutosave();
    }, 1);
  }

  private setupAutosave(): void {
    this.autosaveSubscription = this.form.valueChanges.pipe(
      tap(value => console.log(value)),
      debounceTime(800),
      filter(() => !this.readonly && (this.status === 'DRAFT' || this.status === 'IN_PROGRESS')),
      switchMap(() => {
        this.dynamicFormService.setAutosaving(true);
        const context = new HttpContext().set(SKIP_ERROR_TOAST, true);
        return this.tasksService.sendTask(
          this.store.selectedProject.id,
          this.taskId,
          { formEntries: this.prepareFormEntries() },
          true,
          context
        ).pipe(
          retry({ count: 2, delay: 3000 }),
          finalize(() => this.dynamicFormService.setAutosaving(false)),
          catchError(() => EMPTY)
        );
      })
    ).subscribe(task => this.updateFormAfterAutosave(task));
  }

  private updateFormAfterAutosave(task: Task): void {
    if (task.form?.entries) {
      this.patchFailureDescriptions(task.form.entries, this.form);
    }
    this.prepareCustomHeaders(task);
    this.initialFormValue = this.form.getRawValue();
  }

  private patchFailureDescriptions(entries: FormEntry[], group: FormGroup): void {
    entries.forEach(entry => {
      const entryGroup = group.get(getFormEntryControlName(entry.templateFormEntryId, entry.cloneIndex)) as FormGroup;
      if (!entryGroup) return;
      if (entry.failureDescription !== undefined) {
        entryGroup.get('failureDescription')?.setValue(entry.failureDescription, { emitEvent: false });
      }
      if (entry.children?.length) {
        this.patchFailureDescriptions(entry.children, entryGroup);
      }
    });
  }
  public ngOnDestroy() {
    this.store.showProjectSelector();

    if (this.formListener$) {
      this.formListener$.unsubscribe();
    }
    if (this.autosaveSubscription) {
      this.autosaveSubscription.unsubscribe();
    }
    this.dynamicFormService.setAutosaving(false);
  }

  private loadTask(task?: Task) {
    if(task){
      this.task$.next(task);
    }
    else {
      this.tasksService.getTask(this.store.selectedProject.id, this.taskId)
      .pipe(take(1))
      .subscribe(task => this.task$.next(task));
    }
  }

  protected onSubmit() {
    this.form.markAllAsTouched();
    this.validationRefreshTick++;

    if (this.status === 'DRAFT') {
      this.store.showWarningMsg('TASKS.SAVE_BEFORE_SEND')
      return;
    }

    if (!this.form.valid) {
      this.store.showWarningMsg('TASKS.INVALID_FORM');
      return;
    }

    this.store.showLoader();

    if (this.status === 'IN_PROGRESS') {
      this.handleSendTask();
    }

    if (this.status === 'TO_ACCEPT') {
      this.handleCloseTask();
    }
  }

  private convertFormValue(value: Record<string|number, any>, entryKey: string): FormEntryValue {
    const [valueKeys, childrenKeys] = partition(Object.keys(value), key => !key.startsWith('_') && key !== 'failureDescription');
    const { id, cloneIndex } = parseFormEntryInstanceKey(entryKey);

    return {
      id,
      cloneIndex,
      values: valueKeys.map(key => ({ id: Number(key), value: value[key] })),
      children: childrenKeys.filter(key => key !== 'failureDescription').map(key => this.convertFormValue(value[key], key.replace('_', ''))),
      failureDescription: value['failureDescription'],
    }
  }

  protected save() {
    if (this.status === 'TO_ACCEPT' && this.form.invalid) {
      this.form.markAllAsTouched();
      this.validationRefreshTick++;
      this.store.showWarningMsg('TASKS.INVALID_FORM');
      return;
    }

    this.store.showLoader();
    this.failureId = undefined;
    this.tasksService.sendTask(this.store.selectedProject.id, this.taskId, { formEntries: this.prepareFormEntries() }, true)
      .pipe(
        tap(() => {
          this.store.showSuccessMsg('TASKS.SAVE_SUCCESS');
          this.store.hideLoader();
        }),
      )
      .subscribe(task => this.loadTask(task));
  }

  private handleCloseTask(): void {
    this.tasksService.closeTask(this.taskId, { formEntries: this.prepareFormEntries() } ).pipe(
      tap(() => {
        this.store.showSuccessMsg('TASKS.CLOSE_SUCCESS');
        this.initialFormValue = this.form?.getRawValue();
        void this.router.navigate([GO_URL, TASKS_URL]);
        this.store.hideLoader();
      }),
      tap(() => this.tasksService.refreshTasks())
    ).subscribe()
  }

  private handleSendTask(): void {
    this.tasksService.sendTask(this.store.selectedProject.id, this.taskId, { formEntries: this.prepareFormEntries() }, false ).pipe(
      tap(() => {
        this.store.showSuccessMsg('TASKS.SEND_SUCCESS');
        this.initialFormValue = this.form?.getRawValue();
        void this.router.navigate([GO_URL, TASKS_URL]);
        this.store.hideLoader();
      }),
      tap(() => this.tasksService.refreshTasks())
    ).subscribe()
  }

  private prepareFormEntries(): FormEntryValue[] {
    const value = this.readonly ? this.form.getRawValue() : this.form.value;
    return Object.keys(value).filter(key => key !== 'failureDescription').map(key => this.convertFormValue(value[key], key.replace('_', '')))
  }

  protected close() {
    void this.router.navigate([GO_URL, TASKS_URL]);
  }

  public hasUnsavedChanges(): boolean {
    return !this.readonly && JSON.stringify(this.form?.getRawValue()) !== JSON.stringify(this.initialFormValue);
  }

  public saveChanges() {
    this.store.showLoader();
    return this.tasksService.sendTask(
      this.store.selectedProject.id,
      this.taskId,
      { formEntries: this.prepareFormEntries() },
      true
    ).pipe(
      finalize(() => {
        this.store.hideLoader();
        this.store.showSuccessMsg('TASKS.SAVE_SUCCESS');
      }),
    );
  }

  private initializeTaskView(data: Task): void {
    this.setReadOnly(data);
    this.setShowActionButtons(data.status);
    this.handleSubmitButton(data.status);
    this.setShowSaveButton(data.status);

    this.showFillInButton = this.isFillButtonAvailable(data);
  }

  private setReadOnly(task: Task): void {
    if (task.status === 'DRAFT' || task.status === 'IN_PROGRESS') {
      if ((this.store.hasTaskApproveRole() || this.store.hasTaskEditRole()) && !this.store.hasTaskReceiveRole()) {
        this.readonly = true;
      } else {
        this.readonly = !(
          this.store.isUserAssignedToTask(task.assignedTo ? [task.assignedTo] : []) ||
          (!task.assignedTo && this.store.isUserAssignedToTask(task.assignees)) ||
          (!task.assignedTo && this.store.isAdmin())
        );
      }
    } else if (task.status === 'TO_ACCEPT' && this.store.hasTaskEditRole()) {
      this.readonly = false;
    }
  }

  private setShowActionButtons(status: TaskStatus): void {
    if (!this.readonly && status !== 'CLOSED') {
      this.showActionButtons = true;
    } else this.showActionButtons = status === 'TO_ACCEPT' && this.store.hasTaskApproveRole();
  }

  private handleSubmitButton(status: TaskStatus): void {
    if (status === 'IN_PROGRESS' || status === 'DRAFT') {
      this.submitLabel = 'SEND';
      this.showSubmitButton = true;
    } else if (status === 'TO_ACCEPT' && this.store.hasTaskApproveRole()) {
      this.submitLabel = 'CONFIRM';
      this.showSubmitButton = true;
    } else {
      this.showSubmitButton = false;
    }
  }

  private setShowSaveButton(status: TaskStatus): void {
    this.showSaveButton = !(status === 'TO_ACCEPT' && this.store.hasTaskApproveRole() && !this.store.hasTaskEditRole());
  }

  private prepareCustomHeaders(data: Task): void {
    const { name, status, modifiedBy, modifiedOn, createdBy, createdOn } = data;

    this.customHeaders = [
      {
        key: this.translate.instant('TASK_NAME'),
        value: name,
      },
      {
        key: this.translate.instant('STATUS'),
        value: this.translate.instant(`TASKS.STATUS.${status}`),
      },
      {
        key: this.translate.instant('CREATED_BY'),
        value: createdBy ? userOptionLabel(createdBy) : '---',
      },
      {
        key: this.translate.instant('CREATED_ON'),
        value:  createdOn ? getLocalDate(createdOn, DATE_TIME_FORMAT) : '---',
      },
      {
        key: this.translate.instant('MODIFIED_BY'),
        value: modifiedBy ? userOptionLabel(modifiedBy) : '---',
      },
      {
        key: this.translate.instant('MODIFIED_ON'),
        value: modifiedOn ? getLocalDate(modifiedOn, DATE_TIME_FORMAT) : '---',
      },
    ];
  }

  protected openPdf(pdf: PdfBaseModel) {
    this.store.showLoader();
    this.attachmentsService.download(pdf.linePdfId).pipe(
      tap(blob => openFileInNewWindow(blob)),
      finalize(() => this.store.hideLoader()),
    ).subscribe()
  }

  protected fillIn() {
    if (this.controlsHaveDefaultValue) {
      this.dynamicFormService.triggerFillInForm();
      this.store.showSuccessMsg('FILL_IN_SUCCESS');
    } else {
      this.store.showWarningMsg('FILL_IN_NO_DEFAULT_VALUE');
    }
  }

  private checkIfControlsHaveDefaultValue(task: Task): boolean {
    if (task.form?.entries) {
      return this.hasAnyDefaultValue(task.form?.entries);
    }

    return false;
  }

  private hasAnyDefaultValue(formEntries: FormEntry[]): boolean {
    return formEntries.some(entry => {
      if (entry.control?.some(ctrl => this.controlHasDefaultValue(ctrl))) {
        return true;
      }

      return entry.children ? this.hasAnyDefaultValue(entry.children) : false;
    });
  }

  private controlHasDefaultValue(control: Control): boolean {
    if (control.options && control.options.some(option => option.defaultValue)) {
      return true;
    }

    return control.controls?.some(child => this.controlHasDefaultValue(child)) ?? false;
  }

  private isFillButtonAvailable(task: Task): boolean {
     return (task.status === 'IN_PROGRESS' || task.status === 'DRAFT') && this.store.hasTaskReceiveProRole() && task.templateImplementationVersion > 1;
  }
}
